import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getIntegrationStatus } from '@/modules/common/api/integration-status';
import { FeatureMode } from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationStatusDescriptor } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';

import { getAvailableIntegrations } from '../api/integrations.api';
import { ProjectIntegration } from '../types/project-integration';

export interface UseProjectIntegrationsParams {
  mode: FeatureMode;
  projectId?: number;
  rawIntegrations?: IntegrationInfoResponse[];
}

/**
 * Maps an integration status descriptor to a ProjectIntegration with allProjectsLinked=true.
 * Used when /unlinked returns empty in Create mode — the integration is connected but all
 * external projects are already linked to other projects in the app.
 */
const mapStatusDescriptorToProjectIntegration = (
  descriptor: IntegrationStatusDescriptor
): ProjectIntegration => {
  const service = descriptor.integration as IntegrationServices;
  return {
    id: service,
    type: service,
    icon: getIntegrationIcon(service),
    projects: [],
    selectedValue: null,
    allProjectsLinked: true,
  };
};

/**
 * Hook to fetch available integrations for projects.
 * In Create mode, fetches only unlinked integrations. When the unlinked list is empty,
 * falls back to /api/integrations/status to show integrations that have all projects
 * already linked (with a disabled selector and informational tooltip).
 * In Edit/Read mode, merges unlinked integrations with selected ones from project data.
 */
export const useProjectIntegrations = ({
  mode,
  projectId,
  rawIntegrations,
}: UseProjectIntegrationsParams): UseQueryResult<ProjectIntegration[], Error> => {
  return useQuery({
    queryKey: [
      'available-project-integrations',
      mode,
      projectId,
      (rawIntegrations ?? []).map(i => ({
        p: i.integrationProviderType,
        c: i.connectedIntegration?.id ?? null,
      })),
    ],
    queryFn: async () => {
      const selectedIntegrations = mode !== FeatureMode.Create ? rawIntegrations : undefined;
      const unlinked = await getAvailableIntegrations(selectedIntegrations);

      if (mode === FeatureMode.Create && unlinked.length === 0) {
        const statusData = await getIntegrationStatus();

        // Keep only integrations that support the PROJECT entity type
        const projectIntegrations = statusData.integrations.filter(descriptor =>
          descriptor.entities.some(entity => entity.entityType === EntityType.Project)
        );

        return projectIntegrations
          .map(mapStatusDescriptorToProjectIntegration)
          .sort((a, b) => a.type.localeCompare(b.type));
      }

      return unlinked;
    },
  });
};
